import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  HiCalendar,
  HiChevronLeft,
  HiChevronRight,
  HiChartBar,
  HiX,
  HiArrowUp,
  HiArrowDown,
  HiCursorClick,
  HiPlus,
  HiFilter,
  HiViewGrid,
  HiChevronDown,
} from 'react-icons/hi';
import apiClient from '../api/client';
import ModalOverlay from '../components/ModalOverlay';
import ChartPanel, { ChartErrorBoundary } from '../components/ChartPanel';
import CreateCampaignModal from '../components/CreateCampaignModal';
import PeriodDateRangeModal, {
  applyComparePreset,
  computePreviousPeriod,
  defaultRange,
  formatDateRange,
} from '../components/PeriodDateRangeModal';

const FUNNEL_PAGE_SIZE = 500;

const metricOptions = [
  { key: 'open_card_count', label: 'Просмотры', format: (value) => value?.toLocaleString('ru-RU') ?? '0' },
  { key: 'add_to_cart_count', label: 'В корзину', format: (value) => value?.toLocaleString('ru-RU') ?? '0' },
  { key: 'orders_count', label: 'Заказы', format: (value) => value?.toLocaleString('ru-RU') ?? '0' },
  { key: 'orders_sum_rub', label: 'Выручка, ₽', format: (value) =>
      value !== null ? value.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '0' },
  { key: 'ad_spend', label: 'Расход, ₽', format: (value) => (value != null ? value.toLocaleString('ru-RU', { maximumFractionDigits: 0 }) : '0') },
  { key: 'drr', label: 'ДРР, %', format: (value) => (value != null ? `${Number(value).toFixed(2)}%` : '—') },
  { key: 'ad_share', label: 'Доля рекламы', format: (value) => (value != null ? `${Number(value).toFixed(2)}%` : '—') },
  { key: 'open_to_cart_rate', label: 'Конв. карт. → корз., %', format: (value) => (value !== null ? `${Number(value).toFixed(2)}%` : '0%') },
  { key: 'cart_to_order_rate', label: 'Конв. корз. → заказ, %', format: (value) => (value !== null ? `${Number(value).toFixed(2)}%` : '0%') },
  { key: 'open_to_order_rate', label: 'Конв. карт. → заказ, %', format: (value) => (value !== null ? `${Number(value).toFixed(2)}%` : '0%') },
  { key: 'avg_check', label: 'Средний чек, ₽', format: (value) => (value !== null ? Number(value).toFixed(2) : '—') },
];

const metricColors = {
  open_card_count: '#4f46e5',
  add_to_cart_count: '#ef4444',
  orders_count: '#10b981',
  orders_sum_rub: '#f97316',
  ad_spend: '#dc2626',
  drr: '#0891b2',
  ad_share: '#7c3aed',
  open_to_cart_rate: '#6366f1',
  cart_to_order_rate: '#0ea5e9',
  open_to_order_rate: '#ec4899',
  avg_check: '#8b5cf6',
};

const tableGroupingOptions = [
  { key: 'skleyka', label: 'Склейка' },
  { key: 'card', label: 'Карточка' },
];

const fmtInt = (v) => (v != null ? Number(v).toLocaleString('ru-RU') : '—');
const fmtMoney = (v) => (v != null ? Number(v).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '—');
const fmtPct = (v) => (v != null && Number.isFinite(Number(v)) ? `${Number(v).toFixed(1)}%` : '—');
const fmtPct2 = (v) => (v != null && Number.isFinite(Number(v)) ? `${Number(v).toFixed(2)}%` : '—');
const fmtAvgCheck = (v) => (v != null && Number.isFinite(Number(v)) ? Number(v).toFixed(2) : '—');

/** Метрики, где рост — хуже (красный) */
const LOWER_IS_BETTER_COMPARE_METRICS = new Set(['drr', 'drr_total', 'drr_ad']);

const getCompareDeltaColorClass = (metricKey, delta) => {
  const isPositive = delta >= 0;
  if (LOWER_IS_BETTER_COMPARE_METRICS.has(metricKey)) {
    return isPositive ? 'text-red-600' : 'text-green-600';
  }
  return isPositive ? 'text-green-600' : 'text-red-600';
};

const getDelta = (current, compare) => {
  if (compare === null || compare === undefined) return null;
  const cur = Number(current) || 0;
  const cmp = Number(compare);
  if (!Number.isFinite(cmp)) return null;
  const delta = cur - cmp;
  const percent = cmp !== 0 ? (delta / cmp) * 100 : (cur !== 0 ? 100 : 0);
  return { delta, percent };
};

const formatWithDelta = (current, compare, formatter, metricKey) => {
  const deltaData = getDelta(current, compare);
  if (deltaData === null) {
    return (
      <div className="flex flex-col gap-0.5">
        <div className="font-medium">{formatter(current)}</div>
      </div>
    );
  }
  const { delta, percent } = deltaData;
  const isPositive = delta >= 0;
  const percentText = `${isPositive ? '+' : ''}${percent.toFixed(1)}%`;
  const deltaColorClass = metricKey
    ? getCompareDeltaColorClass(metricKey, delta)
    : (isPositive ? 'text-green-600' : 'text-red-600');

  return (
    <div className="flex flex-col gap-0.5">
      <div className="font-medium">{formatter(current)}</div>
      <div className={`text-xs font-medium ${deltaColorClass}`}>{percentText}</div>
      <div className="text-xs text-gray-500">{formatter(compare)}</div>
    </div>
  );
};

/** Агрегация compareMetrics по карточкам склейки */
function aggregateCompareMetrics(cards) {
  const hasAny = cards.some((c) => c.compareMetrics);
  if (!hasAny) return null;
  const agg = cards.reduce(
    (acc, r) => {
      const m = r.compareMetrics;
      if (!m) return acc;
      acc.open_card_count += Number(m.open_card_count) || 0;
      acc.add_to_cart_count += Number(m.add_to_cart_count) || 0;
      acc.orders_count += Number(m.orders_count) || 0;
      acc.orders_sum_rub += Number(m.orders_sum_rub) || 0;
      acc.ad_spend += Number(m.ad_spend) || 0;
      acc.ad_orders += Number(m.ad_orders) || 0;
      acc.ad_revenue += Number(m.ad_revenue) || 0;
      acc.organic_orders += Number(m.organic_orders) || 0;
      acc.organic_revenue += Number(m.organic_revenue) || 0;
      return acc;
    },
    {
      open_card_count: 0,
      add_to_cart_count: 0,
      orders_count: 0,
      orders_sum_rub: 0,
      ad_spend: 0,
      ad_orders: 0,
      ad_revenue: 0,
      organic_orders: 0,
      organic_revenue: 0,
    },
  );
  return {
    ...agg,
    drr_total: agg.orders_sum_rub > 0 ? (agg.ad_spend / agg.orders_sum_rub) * 100 : null,
    drr_ad: agg.ad_revenue > 0 ? (agg.ad_spend / agg.ad_revenue) * 100 : null,
    ad_share: agg.orders_count > 0 ? (agg.ad_orders / agg.orders_count) * 100 : null,
    ad_avg_check: agg.ad_orders > 0 ? agg.ad_revenue / agg.ad_orders : null,
    organic_avg_check: agg.organic_orders > 0 ? agg.organic_revenue / agg.organic_orders : null,
    funnel: {
      open_to_cart_rate: agg.open_card_count > 0 ? (agg.add_to_cart_count / agg.open_card_count) * 100 : null,
      cart_to_order_rate: agg.add_to_cart_count > 0 ? (agg.orders_count / agg.add_to_cart_count) * 100 : null,
      open_to_order_rate: agg.open_card_count > 0 ? (agg.orders_count / agg.open_card_count) * 100 : null,
      avg_check: agg.orders_count > 0 ? agg.orders_sum_rub / agg.orders_count : null,
    },
  };
}
const FUNNEL_COL_COUNT = 15;

/** Ориентиры WB, % — если в таблице мало строк, сравниваем с ними */
const CONVERSION_BENCHMARKS = {
  open_to_cart_rate: { low: 4, high: 10 },
  cart_to_order_rate: { low: 12, high: 28 },
  open_to_order_rate: { low: 0.8, high: 2.5 },
  ad_share: { low: 20, high: 50 },
};

function percentileThresholds(values) {
  const sorted = [...values].sort((a, b) => a - b);
  const pick = (pct) => {
    const idx = Math.min(sorted.length - 1, Math.max(0, Math.ceil((pct / 100) * sorted.length) - 1));
    return sorted[idx];
  };
  return { low: pick(33), high: pick(66) };
}

function getConversionThresholds(rows, metricKey, getValue) {
  const values = rows
    .map(getValue)
    .filter((v) => v != null && Number.isFinite(Number(v)))
    .map(Number);
  if (values.length >= 5) {
    return percentileThresholds(values);
  }
  return CONVERSION_BENCHMARKS[metricKey] || { low: 0, high: 100 };
}

function getConversionHeatClass(value, thresholds) {
  if (value == null || !Number.isFinite(Number(value))) {
    return 'bg-gray-100 text-gray-500';
  }
  const v = Number(value);
  if (v >= thresholds.high) {
    return 'bg-emerald-100 text-emerald-800 font-semibold';
  }
  if (v >= thresholds.low) {
    return 'bg-amber-100 text-amber-900 font-medium';
  }
  return 'bg-red-100 text-red-800 font-medium';
}

function ConversionRateCell({ value, compare, thresholds, metricKey }) {
  const heatClass = getConversionHeatClass(value, thresholds);
  const text =
    value != null && Number.isFinite(Number(value)) ? `${Number(value).toFixed(2)}%` : '—';
  const deltaData = getDelta(value, compare);
  return (
    <td className="py-2 text-gray-700">
      <div className="flex flex-col gap-0.5 items-start">
        <span
          className={`inline-block min-w-[3.25rem] text-center px-2 py-0.5 rounded-md text-xs tabular-nums leading-tight ${heatClass}`}
        >
          {text}
        </span>
        {deltaData && (
          <>
            <div className={`text-xs font-medium ${getCompareDeltaColorClass(metricKey || 'rate', deltaData.delta)}`}>
              {`${deltaData.delta >= 0 ? '+' : ''}${deltaData.percent.toFixed(1)}%`}
            </div>
            <div className="text-xs text-gray-500">{fmtPct2(compare)}</div>
          </>
        )}
      </div>
    </td>
  );
}

function ChartMetricToggle({ metric, selected, onToggle }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle(metric);
      }}
      className={`p-1 rounded transition-colors ${
        selected
          ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
          : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
      }`}
      title={selected ? 'Убрать с графика' : 'Добавить на график'}
    >
      {selected ? <HiX className="w-4 h-4" /> : <HiChartBar className="w-4 h-4" />}
    </button>
  );
}

function parseArticleFilterTokens(value) {
  if (!value) return { nmIds: [], vendorTokens: [] };
  const tokens = String(value)
    .split(/[\s,;]+/g)
    .map((t) => t.trim())
    .filter(Boolean);

  const nmIds = [];
  const vendorTokens = [];
  const seenNm = new Set();
  const seenVendor = new Set();

  for (const t of tokens) {
    if (/^\d+$/.test(t)) {
      const n = Number(t);
      if (!Number.isFinite(n) || seenNm.has(n)) continue;
      seenNm.add(n);
      nmIds.push(n);
    } else {
      const key = t.toLowerCase();
      if (seenVendor.has(key)) continue;
      seenVendor.add(key);
      vendorTokens.push(key);
    }
  }
  return { nmIds, vendorTokens };
}

export default function SalesFunnelStatistics({ clientId }) {
  const initialRange = defaultRange();
  const initialCompare = computePreviousPeriod(initialRange.from, initialRange.to);
  const [periodFrom, setPeriodFrom] = useState(initialRange.from);
  const [periodTo, setPeriodTo] = useState(initialRange.to);
  const [compareFrom, setCompareFrom] = useState(initialCompare.from);
  const [compareTo, setCompareTo] = useState(initialCompare.to);
  const [compareDirty, setCompareDirty] = useState(false);
  const [comparePreset, setComparePreset] = useState('previous');
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [groupBy, setGroupBy] = useState('day');
  const [selectedMetrics, setSelectedMetrics] = useState(['orders_count', 'orders_sum_rub']);
  const [showCompareLines, setShowCompareLines] = useState(true);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [funnelItems, setFunnelItems] = useState([]);
  const [funnelLoading, setFunnelLoading] = useState(false);
  const [funnelError, setFunnelError] = useState('');
  const [funnelPage, setFunnelPage] = useState(1);
  const [funnelTotal, setFunnelTotal] = useState(0);
  const [funnelTotalPages, setFunnelTotalPages] = useState(0);
  const [sortColumn, setSortColumn] = useState('orders_sum_rub');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedCardNmIds, setSelectedCardNmIds] = useState(() => new Set());
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [campaignModalInitialRows, setCampaignModalInitialRows] = useState([]);
  const [imagePreview, setImagePreview] = useState({ url: null, x: 0, y: 0 });
  const [nmIdsWithCampaigns, setNmIdsWithCampaigns] = useState(() => new Set());
  const [cardNmIdsFilterText, setCardNmIdsFilterText] = useState('');
  const [cardNmIdsFilterOpen, setCardNmIdsFilterOpen] = useState(false);
  const [cardFilterDebounced, setCardFilterDebounced] = useState('');
  const cardFilterDebounceRef = useRef(null);
  const [expandedCards, setExpandedCards] = useState(() => new Set());
  const [tableGroupOrder, setTableGroupOrder] = useState(['card', 'skleyka']);
  const [groupingModalOpen, setGroupingModalOpen] = useState(false);
  const [dragGroupingKey, setDragGroupingKey] = useState(null);
  const [expandedSkleyki, setExpandedSkleyki] = useState(() => new Set());
  const [skleykaLabels, setSkleykaLabels] = useState({});

  const groupBySkleyka = tableGroupOrder[0] === 'skleyka';

  const moveGrouping = useCallback((dragKey, targetKey) => {
    if (!dragKey || !targetKey || dragKey === targetKey) return;
    setTableGroupOrder((prev) => {
      const next = [...prev];
      const from = next.indexOf(dragKey);
      const to = next.indexOf(targetKey);
      if (from < 0 || to < 0) return prev;
      next.splice(from, 1);
      next.splice(to, 0, dragKey);
      return next;
    });
  }, []);

  useEffect(() => {
    if (cardFilterDebounceRef.current) clearTimeout(cardFilterDebounceRef.current);
    cardFilterDebounceRef.current = setTimeout(() => {
      setCardFilterDebounced(cardNmIdsFilterText);
      setFunnelPage(1);
    }, 400);
    return () => clearTimeout(cardFilterDebounceRef.current);
  }, [cardNmIdsFilterText]);

  useEffect(() => {
    if (compareDirty || comparePreset === 'manual') return;
    if (!periodFrom || !periodTo) return;
    const range = applyComparePreset(comparePreset, periodFrom, periodTo);
    if (!range) return;
    setCompareFrom(range.from);
    setCompareTo(range.to);
  }, [periodFrom, periodTo, compareDirty, comparePreset]);

  const handleDateApply = ({
    periodFrom: nextFrom,
    periodTo: nextTo,
    compareFrom: nextCompareFrom,
    compareTo: nextCompareTo,
    compareDirty: nextCompareDirty,
    comparePreset: nextComparePreset,
  }) => {
    setPeriodFrom(nextFrom);
    setPeriodTo(nextTo);
    setCompareFrom(nextCompareFrom);
    setCompareTo(nextCompareTo);
    setCompareDirty(nextCompareDirty);
    setComparePreset(nextComparePreset);
  };

  const fetchSummary = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    setError('');
    try {
      const params = {
        client_id: clientId,
        from: periodFrom,
        to: periodTo,
        group_by: groupBy,
      };
      if (compareFrom && compareTo) {
        params.compare_from = compareFrom;
        params.compare_to = compareTo;
      }
      const { data } = await apiClient.get('/reports/sales-funnel-stats', { params });
      setStats(data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Не удалось загрузить статистику');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [clientId, periodFrom, periodTo, compareFrom, compareTo, groupBy]);

  useEffect(() => {
    if (clientId) {
      fetchSummary();
    }
  }, [clientId, fetchSummary]);

  const fetchFunnelItems = useCallback(async () => {
    if (!clientId) return;
    setFunnelLoading(true);
    setFunnelError('');
    try {
      const { nmIds, vendorTokens } = parseArticleFilterTokens(cardFilterDebounced);
      const params = {
        client_id: String(clientId),
        from: periodFrom,
        to: periodTo,
        page: funnelPage,
        page_size: FUNNEL_PAGE_SIZE,
      };
      if (compareFrom && compareTo) {
        params.compare_from = compareFrom;
        params.compare_to = compareTo;
      }
      if (nmIds.length) params.nm_ids = nmIds.join(',');
      if (vendorTokens.length) params.vendor_codes = vendorTokens.join(',');
      if (sortColumn && sortColumn !== 'stock_count') {
        params.sort_by = sortColumn;
        params.sort_dir = sortDirection || 'desc';
      }
      const { data } = await apiClient.get('/reports/sales-funnel', { params });
      setFunnelItems(Array.isArray(data.items) ? data.items : []);
      setFunnelTotal(Number(data.total_items) || 0);
      setFunnelTotalPages(Number(data.total_pages) || 0);
    } catch (err) {
      console.error('Ошибка загрузки списка карточек:', err);
      setFunnelItems([]);
      setFunnelTotal(0);
      setFunnelTotalPages(0);
      setFunnelError(err.response?.data?.message || 'Не удалось загрузить список карточек');
    } finally {
      setFunnelLoading(false);
    }
  }, [clientId, periodFrom, periodTo, compareFrom, compareTo, funnelPage, cardFilterDebounced, sortColumn, sortDirection]);

  useEffect(() => {
    setFunnelPage(1);
  }, [periodFrom, periodTo, compareFrom, compareTo, clientId]);

  const fetchNmWithCampaigns = useCallback(async () => {
    if (!clientId) return;
    try {
      const { data } = await apiClient.get('/reports/nm-with-campaigns', {
        params: { client_id: String(clientId) },
      });
      setNmIdsWithCampaigns(new Set(Array.isArray(data?.nm_ids) ? data.nm_ids : []));
    } catch (err) {
      console.error('Ошибка загрузки артикулов с рекламой:', err);
      setNmIdsWithCampaigns(new Set());
    }
  }, [clientId]);

  useEffect(() => {
    if (clientId && periodFrom && periodTo) {
      fetchFunnelItems();
    }
  }, [clientId, periodFrom, periodTo, fetchFunnelItems]);

  useEffect(() => {
    if (clientId) {
      fetchNmWithCampaigns();
    }
  }, [clientId, fetchNmWithCampaigns]);

  // Карта imt_id → название склейки (бренд · предмет) для группировки
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    apiClient
      .get('/skleyki/tree', { params: { client_id: String(clientId) } })
      .then(({ data }) => {
        if (cancelled) return;
        const map = {};
        (data?.groups || []).forEach((g) => {
          const label = [g.brand, g.subject_name].filter(Boolean).join(' · ') || `Склейка ${g.imt_id}`;
          map[Number(g.imt_id)] = label;
        });
        setSkleykaLabels(map);
      })
      .catch(() => setSkleykaLabels({}));
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // Плашка «Реклама» — скользящие 14 дней на бэкенде (CURDATE()); обновляем при возврате на вкладку и по таймеру
  useEffect(() => {
    if (!clientId) return undefined;

    const refreshAdBadges = () => {
      fetchNmWithCampaigns();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshAdBadges();
    };
    document.addEventListener('visibilitychange', onVisibility);

    const interval = setInterval(refreshAdBadges, 5 * 60 * 1000);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(interval);
    };
  }, [clientId, fetchNmWithCampaigns]);

  useEffect(() => {
    if (!clientId) {
      return;
    }
    const interval = setInterval(() => {
      fetchSummary(false);
    }, 5 * 60 * 1000);
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [clientId, fetchSummary]);

  const chartData = useMemo(() => {
    if (!stats?.timeSeries?.period?.length && !stats?.timeSeries?.compare?.length) {
      return [];
    }

    const primarySeries =
      stats.timeSeries.period.length > 0 ? stats.timeSeries.period : stats.timeSeries.compare ?? [];
    const secondarySeries = stats.timeSeries.compare ?? [];
    return primarySeries.map((row, index) => {
      const compareRow = secondarySeries[index];
      const dataset = { label: row.label };
      selectedMetrics.forEach((metric) => {
        dataset[`period_${metric}`] = row[metric] ?? 0;
        dataset[`compare_${metric}`] = compareRow ? compareRow[metric] ?? 0 : null;
      });
      return dataset;
    });
  }, [stats, selectedMetrics]);

  const handleMetricToggle = (metric) => {
    setSelectedMetrics((prev) =>
      prev.includes(metric) ? prev.filter((item) => item !== metric) : [...prev, metric]
    );
  };

  const handleSort = (column) => {
    setFunnelPage(1);
    if (sortColumn !== column) {
      setSortColumn(column);
      setSortDirection('desc');
    } else if (sortDirection === 'desc') {
      setSortDirection('asc');
    } else if (column === 'orders_sum_rub') {
      setSortDirection('desc');
    } else {
      setSortColumn('orders_sum_rub');
      setSortDirection('desc');
    }
  };

  const displayCardRows = useMemo(() => {
    if (sortColumn !== 'stock_count' || !sortDirection) return funnelItems;
    const rows = [...funnelItems];
    return rows.sort((a, b) => {
      const aVal = a.stock_count ?? 0;
      const bVal = b.stock_count ?? 0;
      return sortDirection === 'desc' ? bVal - aVal : aVal - bVal;
    });
  }, [funnelItems, sortColumn, sortDirection]);

  const hasArticleFilter = Boolean(cardFilterDebounced.trim());
  const articleFilterTokenCount = useMemo(() => {
    const { nmIds, vendorTokens } = parseArticleFilterTokens(cardFilterDebounced);
    return nmIds.length + vendorTokens.length;
  }, [cardFilterDebounced]);

  const filteredCardRows = displayCardRows;

  const toggleCardExpand = useCallback((nmId) => {
    setExpandedCards((prev) => {
      const next = new Set(prev);
      if (next.has(nmId)) next.delete(nmId);
      else next.add(nmId);
      return next;
    });
  }, []);

  const toggleSkleykaExpand = useCallback((imtId) => {
    setExpandedSkleyki((prev) => {
      const next = new Set(prev);
      if (next.has(imtId)) next.delete(imtId);
      else next.add(imtId);
      return next;
    });
  }, []);

  // Группировка карточек по склейкам (imt_id) с агрегированными итогами
  const skleykaGroups = useMemo(() => {
    if (!groupBySkleyka) return [];
    const byImt = new Map();
    for (const row of filteredCardRows) {
      const key = row.imt_id != null ? Number(row.imt_id) : 'none';
      if (!byImt.has(key)) byImt.set(key, []);
      byImt.get(key).push(row);
    }
    const groups = [];
    for (const [key, cards] of byImt.entries()) {
      const agg = cards.reduce(
        (acc, r) => {
          acc.open_card_count += Number(r.open_card_count) || 0;
          acc.add_to_cart_count += Number(r.add_to_cart_count) || 0;
          acc.orders_count += Number(r.orders_count) || 0;
          acc.orders_sum_rub += Number(r.orders_sum_rub) || 0;
          acc.ad_spend += Number(r.ad_spend) || 0;
          acc.ad_orders += Number(r.ad_orders) || 0;
          acc.ad_revenue += Number(r.ad_revenue) || 0;
          acc.organic_orders += Number(r.organic_orders) || 0;
          acc.organic_revenue += Number(r.organic_revenue) || 0;
          acc.stock_count += Number(r.stock_count) || 0;
          return acc;
        },
        {
          open_card_count: 0, add_to_cart_count: 0, orders_count: 0, orders_sum_rub: 0,
          ad_spend: 0, ad_orders: 0, ad_revenue: 0, organic_orders: 0, organic_revenue: 0,
          stock_count: 0,
        },
      );
      const openToCart = agg.open_card_count > 0 ? (agg.add_to_cart_count / agg.open_card_count) * 100 : null;
      const cartToOrder = agg.add_to_cart_count > 0 ? (agg.orders_count / agg.add_to_cart_count) * 100 : null;
      const openToOrder = agg.open_card_count > 0 ? (agg.orders_count / agg.open_card_count) * 100 : null;
      const totals = {
        ...agg,
        avg_check: agg.orders_count > 0 ? agg.orders_sum_rub / agg.orders_count : null,
        drr_total: agg.orders_sum_rub > 0 ? (agg.ad_spend / agg.orders_sum_rub) * 100 : null,
        ad_share: agg.orders_count > 0 ? (agg.ad_orders / agg.orders_count) * 100 : null,
        funnel: {
          open_to_cart_rate: openToCart,
          cart_to_order_rate: cartToOrder,
          open_to_order_rate: openToOrder,
        },
        compareMetrics: aggregateCompareMetrics(cards),
      };
      const label = key === 'none' ? 'Без склейки' : (skleykaLabels[key] || `Склейка ${key}`);
      groups.push({ imt_id: key, label, cards, totals });
    }
    groups.sort((a, b) => (b.totals.orders_sum_rub || 0) - (a.totals.orders_sum_rub || 0));
    return groups;
  }, [groupBySkleyka, filteredCardRows, skleykaLabels]);

  const selectedCardRows = useMemo(() => {
    if (selectedCardNmIds.size === 0) return [];
    return filteredCardRows.filter((row) => selectedCardNmIds.has(row.nm_id));
  }, [filteredCardRows, selectedCardNmIds]);

  const conversionThresholds = useMemo(
    () => ({
      open_to_cart_rate: getConversionThresholds(
        filteredCardRows,
        'open_to_cart_rate',
        (row) => row.funnel?.open_to_cart_rate,
      ),
      cart_to_order_rate: getConversionThresholds(
        filteredCardRows,
        'cart_to_order_rate',
        (row) => row.funnel?.cart_to_order_rate,
      ),
      open_to_order_rate: getConversionThresholds(
        filteredCardRows,
        'open_to_order_rate',
        (row) => row.funnel?.open_to_order_rate,
      ),
      ad_share: getConversionThresholds(
        filteredCardRows,
        'ad_share',
        (row) => row.ad_share,
      ),
    }),
    [filteredCardRows],
  );

  const toggleCardSelection = (nmId) => {
    setSelectedCardNmIds((prev) => {
      const next = new Set(prev);
      if (next.has(nmId)) next.delete(nmId);
      else next.add(nmId);
      return next;
    });
  };

  const toggleSelectAllCards = () => {
    if (filteredCardRows.length === 0) return;
    const allVisibleSelected =
      filteredCardRows.length > 0 &&
      filteredCardRows.every((r) => selectedCardNmIds.has(r.nm_id));

    if (allVisibleSelected) {
      setSelectedCardNmIds((prev) => {
        const next = new Set(prev);
        filteredCardRows.forEach((r) => next.delete(r.nm_id));
        return next;
      });
      return;
    }

    setSelectedCardNmIds((prev) => {
      const next = new Set(prev);
      filteredCardRows.forEach((r) => next.add(r.nm_id));
      return next;
    });
  };

  const openCampaignModal = (rows) => {
    const toSelect = Array.isArray(rows) && rows.length > 0 ? rows : selectedCardRows;
    if (toSelect.length > 0) {
      setCampaignModalInitialRows(toSelect);
      setCampaignModalOpen(true);
    }
  };

  const summaryCards = useMemo(() => {
    if (!stats?.period?.totals) return [];

    const totals = stats.period.totals;
    const compareTotals = stats.compare?.totals;
    
    return [
      {
        label: 'Просмотры',
        key: 'open_card_count',
        value: totals.open_card_count || 0,
        compare: compareTotals?.open_card_count,
        format: (v) => v.toLocaleString('ru-RU'),
      },
      {
        label: 'В корзину',
        key: 'add_to_cart_count',
        value: totals.add_to_cart_count || 0,
        compare: compareTotals?.add_to_cart_count,
        format: (v) => v.toLocaleString('ru-RU'),
      },
      {
        label: 'Заказы',
        key: 'orders_count',
        value: totals.orders_count || 0,
        compare: compareTotals?.orders_count,
        format: (v) => v.toLocaleString('ru-RU'),
      },
      {
        label: 'Выручка',
        key: 'orders_sum_rub',
        value: totals.orders_sum_rub || 0,
        compare: compareTotals?.orders_sum_rub,
        format: (v) => v.toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 0 }),
      },
      {
        label: 'Расход',
        key: 'ad_spend',
        value: totals.ad_spend || 0,
        compare: compareTotals?.ad_spend,
        format: (v) => v.toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 0 }),
      },
      {
        label: 'ДРР, %',
        key: 'drr',
        value: totals.drr || 0,
        compare: compareTotals?.drr,
        format: (v) => `${v.toFixed(2)}%`,
      },
      {
        label: 'Доля рекламы',
        key: 'ad_share',
        value: totals.ad_share || 0,
        compare: compareTotals?.ad_share,
        format: (v) => `${v.toFixed(2)}%`,
      },
      {
        label: 'Конв. карт. → корз.',
        key: 'open_to_cart_rate',
        value: totals.open_to_cart_rate || 0,
        compare: compareTotals?.open_to_cart_rate,
        format: (v) => `${v.toFixed(2)}%`,
      },
      {
        label: 'Конв. корз. → заказ',
        key: 'cart_to_order_rate',
        value: totals.cart_to_order_rate || 0,
        compare: compareTotals?.cart_to_order_rate,
        format: (v) => `${v.toFixed(2)}%`,
      },
      {
        label: 'Конв. карт. → заказ',
        key: 'open_to_order_rate',
        value: totals.open_to_order_rate || 0,
        compare: compareTotals?.open_to_order_rate,
        format: (v) => `${v.toFixed(2)}%`,
      },
      {
        label: 'Средний чек',
        key: 'avg_check',
        value: totals.avg_check || 0,
        compare: compareTotals?.avg_check,
        format: (v) => v !== null && v !== undefined ? `${v.toFixed(2)} ₽` : '—',
      },
    ];
  }, [stats]);

  const renderSubRow = (row, kind) => {
    const isAd = kind === 'ad';
    const cmp = row.compareMetrics;
    const orders = isAd ? row.ad_orders : row.organic_orders;
    const revenue = isAd ? row.ad_revenue : row.organic_revenue;
    const avg = isAd ? row.ad_avg_check : row.organic_avg_check;
    const cmpOrders = isAd ? cmp?.ad_orders : cmp?.organic_orders;
    const cmpRevenue = isAd ? cmp?.ad_revenue : cmp?.organic_revenue;
    const cmpAvg = isAd ? cmp?.ad_avg_check : cmp?.organic_avg_check;
    return (
      <tr key={`${row.nm_id}-${kind}`} className={isAd ? 'bg-rose-50/40' : 'bg-emerald-50/40'}>
        <td />
        <td />
        <td className="py-1.5 pl-9">
          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ${isAd ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
            {isAd ? 'Реклама' : 'Органика'}
          </span>
        </td>
        <td className="py-1.5 text-center text-gray-300">—</td>
        <td className="py-1.5 text-center text-gray-300">—</td>
        <td className="py-1.5 text-center text-gray-300">—</td>
        <td className="py-1.5 text-gray-700 tabular-nums">{formatWithDelta(orders, cmpOrders, fmtInt, 'orders_count')}</td>
        <td className="py-1.5 text-gray-700 tabular-nums">{formatWithDelta(revenue, cmpRevenue, fmtMoney, 'orders_sum_rub')}</td>
        <td className="py-1.5 text-gray-700 tabular-nums">
          {isAd ? formatWithDelta(row.ad_spend, cmp?.ad_spend, fmtMoney, 'ad_spend') : '—'}
        </td>
        <td className="py-1.5 text-gray-700 tabular-nums" title={isAd ? 'ДРР по рекламе = расход / рекл. выручка × 100%' : ''}>
          {isAd ? formatWithDelta(row.drr_ad, cmp?.drr_ad, fmtPct, 'drr_ad') : '—'}
        </td>
        <td className="py-1.5 text-center text-gray-300">—</td>
        <td className="py-1.5 text-center text-gray-300">—</td>
        <td className="py-1.5 text-center text-gray-300">—</td>
        <td className="py-1.5 text-center text-gray-300">—</td>
        <td className="py-1.5 text-gray-700 tabular-nums">{formatWithDelta(avg, cmpAvg, fmtAvgCheck, 'avg_check')}</td>
      </tr>
    );
  };

  const renderCardTr = (row) => {
    const isExpanded = expandedCards.has(row.nm_id);
    const cmp = row.compareMetrics;
    const out = [];
    out.push(
      <tr key={`${row.nm_id}-${row.vendor_code || ''}`} className="hover:bg-gray-50">
        <td className="py-2 text-center">
          <label className="flex items-center justify-center cursor-pointer">
            <input
              type="checkbox"
              checked={selectedCardNmIds.has(row.nm_id)}
              onChange={() => toggleCardSelection(row.nm_id)}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
          </label>
        </td>
        <td className="py-2 align-top">
          {row.main_photo ? (
            <img
              src={row.main_photo}
              alt=""
              className="w-12 h-12 object-contain rounded border border-gray-200 bg-gray-50 cursor-pointer hover:ring-2 hover:ring-indigo-300"
              onMouseEnter={(e) => {
                const rect = e.target.getBoundingClientRect();
                setImagePreview({ url: row.main_photo, x: rect.right + 8, y: rect.top });
              }}
              onMouseLeave={() => setImagePreview((p) => ({ ...p, url: null }))}
            />
          ) : (
            <div className="w-12 h-12 rounded border border-gray-200 bg-gray-100 flex items-center justify-center text-xs text-gray-400">—</div>
          )}
        </td>
        <td className="py-2 max-w-[300px]">
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => toggleCardExpand(row.nm_id)}
              className="shrink-0 p-0.5 text-gray-400 hover:text-gray-700 rounded"
              title="Показать разбивку реклама / органика"
            >
              {isExpanded ? <HiChevronDown className="w-4 h-4" /> : <HiChevronRight className="w-4 h-4" />}
            </button>
            <div className="min-w-0 flex-1">
              <div className="font-medium text-gray-900 truncate" title={row.title || row.vendor_code || String(row.nm_id)}>
                {row.title || row.vendor_code || row.nm_id}
              </div>
              <div className="text-xs text-gray-500 flex items-center gap-1.5 flex-wrap">
                {row.vendor_code || ''}{row.vendor_code ? ' · ' : ''}арт. {row.nm_id}{row.brand_name ? ` · ${row.brand_name}` : ''}
                {nmIdsWithCampaigns.has(row.nm_id) && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800" title="Рекламный расход за последние 2 недели (обновляется автоматически)">
                    <HiCursorClick className="w-3.5 h-3.5 shrink-0" />
                    Реклама
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => openCampaignModal([row])}
              className="shrink-0 p-1.5 text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
              title="Создать кампанию"
            >
              <HiPlus className="w-4 h-4" />
            </button>
          </div>
        </td>
        <td className="py-2 text-gray-700 tabular-nums">{row.stock_count != null ? (row.stock_count).toLocaleString('ru-RU') : '—'}</td>
        <td className="py-2 text-gray-700 tabular-nums">
          {formatWithDelta(row.open_card_count || 0, cmp?.open_card_count, fmtInt, 'open_card_count')}
        </td>
        <td className="py-2 text-gray-700 tabular-nums">
          {formatWithDelta(row.add_to_cart_count || 0, cmp?.add_to_cart_count, fmtInt, 'add_to_cart_count')}
        </td>
        <td className="py-2 text-gray-700 tabular-nums">
          {formatWithDelta(row.orders_count || 0, cmp?.orders_count, fmtInt, 'orders_count')}
        </td>
        <td className="py-2 text-gray-700 tabular-nums">
          {formatWithDelta(row.orders_sum_rub || 0, cmp?.orders_sum_rub, fmtMoney, 'orders_sum_rub')}
        </td>
        <td className="py-2 text-gray-700 tabular-nums">
          {formatWithDelta(row.ad_spend, cmp?.ad_spend, fmtMoney, 'ad_spend')}
        </td>
        <td className="py-2 text-gray-700 tabular-nums" title="ДРР от общего оборота">
          {formatWithDelta(row.drr_total, cmp?.drr_total, fmtPct, 'drr_total')}
        </td>
        <ConversionRateCell
          value={row.ad_share}
          compare={cmp?.ad_share}
          thresholds={conversionThresholds.ad_share}
          metricKey="ad_share"
        />
        <ConversionRateCell
          value={row.funnel?.open_to_cart_rate}
          compare={cmp?.funnel?.open_to_cart_rate}
          thresholds={conversionThresholds.open_to_cart_rate}
          metricKey="open_to_cart_rate"
        />
        <ConversionRateCell
          value={row.funnel?.cart_to_order_rate}
          compare={cmp?.funnel?.cart_to_order_rate}
          thresholds={conversionThresholds.cart_to_order_rate}
          metricKey="cart_to_order_rate"
        />
        <ConversionRateCell
          value={row.funnel?.open_to_order_rate}
          compare={cmp?.funnel?.open_to_order_rate}
          thresholds={conversionThresholds.open_to_order_rate}
          metricKey="open_to_order_rate"
        />
        <td className="py-2 text-gray-700 tabular-nums">
          {formatWithDelta(row.funnel?.avg_check, cmp?.funnel?.avg_check, fmtAvgCheck, 'avg_check')}
        </td>
      </tr>,
    );
    if (isExpanded) {
      out.push(renderSubRow(row, 'ad'));
      out.push(renderSubRow(row, 'organic'));
    }
    return out;
  };

  const renderSkleykaGroup = (group) => {
    const isOpen = expandedSkleyki.has(group.imt_id);
    const t = group.totals;
    const cmp = t.compareMetrics;
    const out = [];
    out.push(
      <tr
        key={`grp-${group.imt_id}`}
        className="bg-indigo-50/70 border-y border-indigo-100 cursor-pointer hover:bg-indigo-100/70"
        onClick={() => toggleSkleykaExpand(group.imt_id)}
      >
        <td />
        <td className="py-2 text-center">
          <span className="text-indigo-500">{isOpen ? <HiChevronDown className="w-4 h-4 inline" /> : <HiChevronRight className="w-4 h-4 inline" />}</span>
        </td>
        <td className="py-2 font-semibold text-indigo-900">
          <span className="inline-flex items-center gap-1.5">
            <HiViewGrid className="w-4 h-4 text-indigo-500" />
            {group.label}
          </span>
          <span className="ml-2 text-xs font-normal text-indigo-500">{group.cards.length} карт.</span>
        </td>
        <td className="py-2 text-indigo-900 tabular-nums">{fmtInt(t.stock_count)}</td>
        <td className="py-2 text-indigo-900 tabular-nums">
          {formatWithDelta(t.open_card_count, cmp?.open_card_count, fmtInt, 'open_card_count')}
        </td>
        <td className="py-2 text-indigo-900 tabular-nums">
          {formatWithDelta(t.add_to_cart_count, cmp?.add_to_cart_count, fmtInt, 'add_to_cart_count')}
        </td>
        <td className="py-2 text-indigo-900 font-medium tabular-nums">
          {formatWithDelta(t.orders_count, cmp?.orders_count, fmtInt, 'orders_count')}
        </td>
        <td className="py-2 text-indigo-900 font-medium tabular-nums">
          {formatWithDelta(t.orders_sum_rub, cmp?.orders_sum_rub, fmtMoney, 'orders_sum_rub')}
        </td>
        <td className="py-2 text-indigo-900 tabular-nums">
          {formatWithDelta(t.ad_spend, cmp?.ad_spend, fmtMoney, 'ad_spend')}
        </td>
        <td className="py-2 text-indigo-900 tabular-nums">
          {formatWithDelta(t.drr_total, cmp?.drr_total, fmtPct, 'drr_total')}
        </td>
        <ConversionRateCell
          value={t.ad_share}
          compare={cmp?.ad_share}
          thresholds={conversionThresholds.ad_share}
          metricKey="ad_share"
        />
        <td className="py-2 text-center text-indigo-900 tabular-nums">
          {formatWithDelta(t.funnel?.open_to_cart_rate, cmp?.funnel?.open_to_cart_rate, fmtPct, 'open_to_cart_rate')}
        </td>
        <td className="py-2 text-center text-indigo-900 tabular-nums">
          {formatWithDelta(t.funnel?.cart_to_order_rate, cmp?.funnel?.cart_to_order_rate, fmtPct, 'cart_to_order_rate')}
        </td>
        <td className="py-2 text-center text-indigo-900 tabular-nums">
          {formatWithDelta(t.funnel?.open_to_order_rate, cmp?.funnel?.open_to_order_rate, fmtPct, 'open_to_order_rate')}
        </td>
        <td className="py-2 text-indigo-900 tabular-nums">
          {formatWithDelta(t.avg_check, cmp?.funnel?.avg_check, fmtAvgCheck, 'avg_check')}
        </td>
      </tr>,
    );
    if (isOpen) {
      group.cards.forEach((c) => {
        renderCardTr(c).forEach((el) => out.push(el));
      });
    }
    return out;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end items-start">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setDateModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
          >
            <HiCalendar className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">
              {formatDateRange(periodFrom, periodTo)}
            </span>
          </button>
        </div>
      </div>

      {loading && (
        <div className="text-center text-gray-600">Загружается статистика...</div>
      )}
      {error && (
        <div className="text-center text-red-600">{error}</div>
      )}

      {stats && (
        <>
          <div className="grid grid-cols-6 gap-3">
            {summaryCards.map((card) => {
              const delta = card.compare !== undefined ? card.value - card.compare : null;
              const deltaPercent = card.compare !== undefined && card.compare !== 0 
                ? ((delta / card.compare) * 100).toFixed(2)
                : null;
              const deltaText = deltaPercent !== null 
                ? `${deltaPercent >= 0 ? '+' : ''}${deltaPercent}%`
                : null;
              
              return (
                <div key={card.key} className="bg-white rounded-lg shadow-sm border border-gray-100 p-3 hover:shadow-md transition-shadow">
                  <div className="mb-1">
                    <p className="text-xs font-medium text-gray-600 truncate" title={card.label}>{card.label}</p>
                  </div>
                  <p className="text-base font-bold text-gray-900 mb-0.5 tabular-nums">{card.format(card.value)}</p>
                  {card.compare !== undefined && deltaText && (
                    <div className="flex items-center gap-1">
                      <span className={`text-xs font-semibold ${delta >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {deltaText}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="rounded-xl border border-white/50 bg-white/85 backdrop-blur-sm shadow-sm p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-700">Динамика</h3>
                <p className="text-sm text-gray-500">
                  {stats.period.from} → {stats.period.to}{' '}
                  {stats.compare && `(сравнение: ${stats.compare.from} → ${stats.compare.to})`}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showCompareLines}
                    onChange={(e) => setShowCompareLines(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  Показать период сравнения
                </label>
                <div className="flex items-center gap-1 p-0.5 rounded-lg bg-slate-100/80">
                  <span className="text-xs text-gray-500 px-2 py-1.5">Группировка:</span>
                  {[
                    { key: 'day', short: 'D', label: 'По дням' },
                    { key: 'week', short: 'W', label: 'По неделям' },
                    { key: 'month', short: 'M', label: 'По месяцам' },
                  ].map(({ key, short, label }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setGroupBy(key)}
                      title={label}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                        groupBy === key
                          ? 'bg-indigo-500 text-white shadow-sm'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-white'
                      }`}
                    >
                      {short}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <ChartPanel height={320}>
              <ChartErrorBoundary label="воронка">
                {chartData.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-sm text-gray-500">
                    Нет данных за выбранный период
                  </div>
                ) : (
                <ResponsiveContainer width="100%" height={320} debounce={50}>
                <ComposedChart data={chartData} margin={{ top: 12, right: 12, left: 8, bottom: 8 }}>
                  <defs>
                    {selectedMetrics.map((metric) => {
                      const color = metricColors[metric] || '#6366f1';
                      return (
                        <linearGradient key={metric} id={`funnel-chart-fill-${metric}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={color} stopOpacity={0.55} />
                          <stop offset="70%" stopColor={color} stopOpacity={0.2} />
                          <stop offset="100%" stopColor={color} stopOpacity={0} />
                        </linearGradient>
                      );
                    })}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    axisLine={{ stroke: '#e2e8f0' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v)}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(30, 41, 59, 0.95)',
                      border: 'none',
                      borderRadius: '10px',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                      padding: '12px 14px',
                    }}
                    labelStyle={{ color: '#94a3b8', fontSize: 12 }}
                    itemStyle={{ color: '#f1f5f9', fontSize: 13 }}
                    formatter={(value, name) => {
                      const metric = String(name).replace('period_', '').replace('compare_', '');
                      const fmt = metricOptions.find((m) => m.key === metric)?.format;
                      return [fmt ? fmt(value) : value, metricOptions.find((m) => m.key === metric)?.label || metric];
                    }}
                  />
                  {selectedMetrics.map((metric) => {
                    const color = metricColors[metric] || '#6366f1';
                    return (
                      <React.Fragment key={metric}>
                        <Area
                          type="monotone"
                          dataKey={`period_${metric}`}
                          name={`period_${metric}`}
                          fill={`url(#funnel-chart-fill-${metric})`}
                          stroke={color}
                          strokeWidth={2.5}
                          baseValue={0}
                          connectNulls
                          isAnimationActive={false}
                        />
                        {showCompareLines && stats.compare && (
                          <Line
                            type="monotone"
                            dataKey={`compare_${metric}`}
                            name={`compare_${metric}`}
                            stroke={color}
                            strokeOpacity={0.6}
                            dot={false}
                            strokeDasharray="5 5"
                            strokeWidth={1.5}
                            connectNulls
                            isAnimationActive={false}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </ComposedChart>
                </ResponsiveContainer>
                )}
              </ChartErrorBoundary>
            </ChartPanel>
          </div>
        </>
      )}

      {clientId && (
        <div className="bg-white rounded-xl shadow p-6 flex flex-col" style={{ maxHeight: 'calc(100vh - 400px)' }}>
          <div className="flex items-center justify-between gap-3 mb-3">
            <button
              type="button"
              onClick={() => setGroupingModalOpen(true)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-sm font-medium transition-colors ${
                groupBySkleyka
                  ? 'bg-indigo-50 border-indigo-400 text-indigo-700 hover:bg-indigo-100'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
              title="Порядок группировки строк таблицы"
            >
              <HiFilter className="w-4 h-4" />
              Группировка строк
              <span className="text-xs font-normal opacity-70">
                ({tableGroupOrder.map((k) => tableGroupingOptions.find((o) => o.key === k)?.label).join(' → ')})
              </span>
            </button>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">
                {hasArticleFilter
                  ? `${funnelTotal} найдено`
                  : `${funnelTotal.toLocaleString('ru-RU')} карточек`}
                {funnelTotalPages > 1 && ` · стр. ${funnelPage} из ${funnelTotalPages}`}
              </span>
              {selectedCardNmIds.size > 0 && (
                <button
                  type="button"
                  onClick={() => openCampaignModal()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <HiPlus className="w-4 h-4" />
                  Создать кампанию ({selectedCardNmIds.size})
                </button>
              )}
            </div>
          </div>
          <div className="overflow-x-auto overflow-y-auto flex-1">
            <table className="w-full text-left text-sm">
              <thead className="text-xs font-medium text-gray-600 border-b sticky top-0 z-10 bg-white">
                <tr>
                  <th className="py-2 bg-white w-10">
                    <label className="flex items-center justify-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={
                          filteredCardRows.length > 0 &&
                          filteredCardRows.every((r) => selectedCardNmIds.has(r.nm_id))
                        }
                        onChange={toggleSelectAllCards}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </label>
                  </th>
                  <th className="py-2 bg-white w-16">Фото</th>
                  <th className="py-2 bg-white">
                    <div className="relative inline-flex items-center gap-2">
                      <span>Карточка</span>
                      <button
                        type="button"
                        className={`p-1 rounded transition-colors ${
                          hasArticleFilter
                            ? 'text-gray-700 hover:bg-gray-100'
                            : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                        }`}
                        title="Фильтр по артикулам WB и продавца"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCardNmIdsFilterOpen((v) => !v);
                        }}
                      >
                        <HiFilter className="w-4 h-4" />
                      </button>

                      {cardNmIdsFilterOpen && (
                        <div
                          className="absolute left-0 top-full mt-2 w-[320px] bg-white border border-gray-200 rounded-xl shadow-lg p-3 z-20"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <div className="text-xs font-semibold text-gray-700">
                              Артикулы WB или продавца
                            </div>
                            <button
                              type="button"
                              className="p-1 rounded hover:bg-gray-100 text-gray-500"
                              title="Закрыть"
                              onClick={() => setCardNmIdsFilterOpen(false)}
                            >
                              <HiX className="w-4 h-4" />
                            </button>
                          </div>

                          <textarea
                            value={cardNmIdsFilterText}
                            onChange={(e) => setCardNmIdsFilterText(e.target.value)}
                            rows={6}
                            className="w-full border border-gray-300 rounded-lg px-2 py-2 text-xs bg-white focus:ring-2 focus:ring-gray-300 focus:border-gray-400 resize-none"
                            placeholder={'Пример:\n12345678\nартикул-продавца\nSKU-001'}
                          />

                          <div className="flex items-center justify-between mt-2">
                            <div className="text-xs text-gray-500">
                              {hasArticleFilter
                                ? `Задано: ${articleFilterTokenCount}, найдено: ${funnelTotal}`
                                : 'Фильтр не задан'}
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                className="text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 text-gray-700"
                                onClick={() => setCardNmIdsFilterText('')}
                                disabled={!cardNmIdsFilterText}
                              >
                                Очистить
                              </button>
                              <button
                                type="button"
                                className="text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 text-gray-700"
                                onClick={() => setCardNmIdsFilterOpen(false)}
                              >
                                Применить
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </th>
                  <th className="py-2 bg-white">
                    <button type="button" onClick={() => handleSort('stock_count')} className="flex items-center gap-1 hover:text-gray-700 transition-colors">
                      Остаток
                      {sortColumn === 'stock_count' && (sortDirection === 'desc' ? <HiArrowDown className="w-4 h-4 text-blue-600" /> : <HiArrowUp className="w-4 h-4 text-blue-600" />)}
                    </button>
                  </th>
                  <th className="py-2 bg-white">
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => handleSort('open_card_count')} className="flex items-center gap-1 hover:text-gray-700 transition-colors">
                        Просмотры
                        {sortColumn === 'open_card_count' && (sortDirection === 'desc' ? <HiArrowDown className="w-4 h-4 text-blue-600" /> : <HiArrowUp className="w-4 h-4 text-blue-600" />)}
                      </button>
                      <ChartMetricToggle metric="open_card_count" selected={selectedMetrics.includes('open_card_count')} onToggle={handleMetricToggle} />
                    </div>
                  </th>
                  <th className="py-2 bg-white">
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => handleSort('add_to_cart_count')} className="flex items-center gap-1 hover:text-gray-700 transition-colors">
                        В корзину
                        {sortColumn === 'add_to_cart_count' && (sortDirection === 'desc' ? <HiArrowDown className="w-4 h-4 text-blue-600" /> : <HiArrowUp className="w-4 h-4 text-blue-600" />)}
                      </button>
                      <ChartMetricToggle metric="add_to_cart_count" selected={selectedMetrics.includes('add_to_cart_count')} onToggle={handleMetricToggle} />
                    </div>
                  </th>
                  <th className="py-2 bg-white">
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => handleSort('orders_count')} className="flex items-center gap-1 hover:text-gray-700 transition-colors">
                        Заказы
                        {sortColumn === 'orders_count' && (sortDirection === 'desc' ? <HiArrowDown className="w-4 h-4 text-blue-600" /> : <HiArrowUp className="w-4 h-4 text-blue-600" />)}
                      </button>
                      <ChartMetricToggle metric="orders_count" selected={selectedMetrics.includes('orders_count')} onToggle={handleMetricToggle} />
                    </div>
                  </th>
                  <th className="py-2 bg-white">
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => handleSort('orders_sum_rub')} className="flex items-center gap-1 hover:text-gray-700 transition-colors">
                        Выручка, ₽
                        {sortColumn === 'orders_sum_rub' && (sortDirection === 'desc' ? <HiArrowDown className="w-4 h-4 text-blue-600" /> : <HiArrowUp className="w-4 h-4 text-blue-600" />)}
                      </button>
                      <ChartMetricToggle metric="orders_sum_rub" selected={selectedMetrics.includes('orders_sum_rub')} onToggle={handleMetricToggle} />
                    </div>
                  </th>
                  <th className="py-2 bg-white">
                    <div className="flex items-center gap-2">
                      <span title="Рекламный расход по артикулу за период">Расход, ₽</span>
                      <ChartMetricToggle metric="ad_spend" selected={selectedMetrics.includes('ad_spend')} onToggle={handleMetricToggle} />
                    </div>
                  </th>
                  <th className="py-2 bg-white">
                    <div className="flex items-center gap-2">
                      <span title="ДРР от общего оборота = рекл. расход / общая выручка × 100%">ДРР, %</span>
                      <ChartMetricToggle metric="drr" selected={selectedMetrics.includes('drr')} onToggle={handleMetricToggle} />
                    </div>
                  </th>
                  <th className="py-2 bg-white" title="Доля рекламы = рекламные заказы / все заказы × 100%">
                    <div className="flex items-center gap-2">
                      <span>Доля рекламы</span>
                      <ChartMetricToggle metric="ad_share" selected={selectedMetrics.includes('ad_share')} onToggle={handleMetricToggle} />
                    </div>
                  </th>
                  <th className="py-2 bg-white">
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => handleSort('open_to_cart_rate')} className="flex items-center gap-1 hover:text-gray-700 transition-colors">
                        Конв. карт. → корз., %
                        {sortColumn === 'open_to_cart_rate' && (sortDirection === 'desc' ? <HiArrowDown className="w-4 h-4 text-blue-600" /> : <HiArrowUp className="w-4 h-4 text-blue-600" />)}
                      </button>
                      <ChartMetricToggle metric="open_to_cart_rate" selected={selectedMetrics.includes('open_to_cart_rate')} onToggle={handleMetricToggle} />
                    </div>
                  </th>
                  <th className="py-2 bg-white">
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => handleSort('cart_to_order_rate')} className="flex items-center gap-1 hover:text-gray-700 transition-colors">
                        Конв. корз. → заказ, %
                        {sortColumn === 'cart_to_order_rate' && (sortDirection === 'desc' ? <HiArrowDown className="w-4 h-4 text-blue-600" /> : <HiArrowUp className="w-4 h-4 text-blue-600" />)}
                      </button>
                      <ChartMetricToggle metric="cart_to_order_rate" selected={selectedMetrics.includes('cart_to_order_rate')} onToggle={handleMetricToggle} />
                    </div>
                  </th>
                  <th className="py-2 bg-white">
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => handleSort('open_to_order_rate')} className="flex items-center gap-1 hover:text-gray-700 transition-colors">
                        Конв. карт. → заказ, %
                        {sortColumn === 'open_to_order_rate' && (sortDirection === 'desc' ? <HiArrowDown className="w-4 h-4 text-blue-600" /> : <HiArrowUp className="w-4 h-4 text-blue-600" />)}
                      </button>
                      <ChartMetricToggle metric="open_to_order_rate" selected={selectedMetrics.includes('open_to_order_rate')} onToggle={handleMetricToggle} />
                    </div>
                  </th>
                  <th className="py-2 bg-white">
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => handleSort('avg_check')} className="flex items-center gap-1 hover:text-gray-700 transition-colors">
                        Средний чек, ₽
                        {sortColumn === 'avg_check' && (sortDirection === 'desc' ? <HiArrowDown className="w-4 h-4 text-blue-600" /> : <HiArrowUp className="w-4 h-4 text-blue-600" />)}
                      </button>
                      <ChartMetricToggle metric="avg_check" selected={selectedMetrics.includes('avg_check')} onToggle={handleMetricToggle} />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {funnelLoading ? (
                  <tr>
                    <td colSpan={FUNNEL_COL_COUNT} className="py-8 text-center text-gray-500">Загрузка списка карточек...</td>
                  </tr>
                ) : funnelError ? (
                  <tr>
                    <td colSpan={FUNNEL_COL_COUNT} className="py-8 text-center text-red-600">{funnelError}</td>
                  </tr>
                ) : funnelTotal === 0 && !funnelLoading ? (
                  <tr>
                    <td colSpan={FUNNEL_COL_COUNT} className="py-8 text-center text-gray-500">
                      Нет карточек в каталоге. Запустите выгрузку: <code className="bg-gray-100 px-1 rounded">node scripts/wb_cards-list.js</code>
                    </td>
                  </tr>
                ) : filteredCardRows.length === 0 ? (
                  <tr>
                    <td colSpan={FUNNEL_COL_COUNT} className="py-8 text-center text-gray-500">
                      По текущему фильтру в колонке «Карточка» ничего не найдено
                    </td>
                  </tr>
                ) : groupBySkleyka ? (
                  skleykaGroups.flatMap((group) => renderSkleykaGroup(group))
                ) : (
                  filteredCardRows.flatMap((row) => renderCardTr(row))
                )}
              </tbody>
            </table>
          </div>
          {funnelTotalPages > 1 && (
            <div className="flex items-center justify-between gap-3 mt-4 pt-3 border-t border-gray-100">
              <span className="text-sm text-gray-500">
                Показано {(funnelPage - 1) * FUNNEL_PAGE_SIZE + 1}–{Math.min(funnelPage * FUNNEL_PAGE_SIZE, funnelTotal)} из {funnelTotal.toLocaleString('ru-RU')}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={funnelPage <= 1 || funnelLoading}
                  onClick={() => setFunnelPage((p) => Math.max(1, p - 1))}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <HiChevronLeft className="w-4 h-4" />
                  Назад
                </button>
                <span className="text-sm text-gray-600 min-w-[80px] text-center">
                  {funnelPage} / {funnelTotalPages}
                </span>
                <button
                  type="button"
                  disabled={funnelPage >= funnelTotalPages || funnelLoading}
                  onClick={() => setFunnelPage((p) => p + 1)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Вперёд
                  <HiChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {!loading && !error && !stats && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-6 text-center text-sm text-gray-500">
          Нет данных за выбранный период. Запустите выгрузку статистики воронки продаж.
        </div>
      )}

      <CreateCampaignModal
        open={campaignModalOpen}
        onClose={() => setCampaignModalOpen(false)}
        initialRows={campaignModalInitialRows}
        clientId={clientId}
        onSuccess={() => setSelectedCardNmIds(new Set())}
      />

      {imagePreview.url && (
        <div className="fixed z-[200] pointer-events-none" style={{ left: imagePreview.x, top: imagePreview.y }}>
          <img src={imagePreview.url} alt="" className="w-48 h-48 min-w-[192px] min-h-[192px] object-contain rounded-lg border-2 border-gray-200 bg-white shadow-2xl" />
        </div>
      )}

      {groupingModalOpen && (
        <ModalOverlay onClick={() => setGroupingModalOpen(false)}>
          <div
            className="modal-panel rounded-2xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Группировка строк</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Перетащите блоки, чтобы изменить порядок вложенности.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setGroupingModalOpen(false)}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
                aria-label="Закрыть"
              >
                <HiX className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-indigo-700/90 mb-4">
              Если сверху «Склейка», карточки группируются по склейкам. Если сверху «Карточка» — плоский список.
            </p>
            <div className="flex flex-wrap gap-2">
              {tableGroupOrder.map((key) => {
                const option = tableGroupingOptions.find((o) => o.key === key);
                return (
                  <button
                    key={key}
                    type="button"
                    draggable
                    onDragStart={() => setDragGroupingKey(key)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      moveGrouping(dragGroupingKey, key);
                      setDragGroupingKey(null);
                    }}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-sm text-gray-700 cursor-move"
                  >
                    <span className="text-slate-400">⋮⋮</span>
                    <span>{option?.label || key}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex justify-end mt-6">
              <button
                type="button"
                onClick={() => setGroupingModalOpen(false)}
                className="glass-btn px-4 py-2 text-sm font-medium"
              >
                Готово
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      <PeriodDateRangeModal
        open={dateModalOpen}
        onClose={() => setDateModalOpen(false)}
        periodFrom={periodFrom}
        periodTo={periodTo}
        compareFrom={compareFrom}
        compareTo={compareTo}
        compareDirty={compareDirty}
        comparePreset={comparePreset}
        onApply={handleDateApply}
      />
    </div>
  );
}
